class ProcessCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiperEl = this.querySelector('.process-carousel__swiper');
    this.titleEl = this.querySelector('[data-carousel-title]');
    this.contentEl = this.querySelector('[data-carousel-content]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.paginationItems = Array.from(this.querySelectorAll('[data-carousel-pagination-item]'));
    this.ctaLink = this.querySelector('[data-carousel-scroll-link]');
  }

  connectedCallback() {
    this.ctaLink?.addEventListener('click', this.onCtaClick);

    if (!this.swiperEl) return;

    // Captured up front, before Swiper touches the DOM: with `loop: true`
    // Swiper clones slides internally for the seamless wrap-around, and
    // there's no guarantee those clones carry custom data-* attributes
    // faithfully. Reading title/content from our own array by `realIndex`
    // (the logical, non-cloned slide position) sidesteps that entirely.
    this.slidesData = Array.from(this.querySelectorAll('.process-carousel__slide')).map((slide) => ({
      title: slide.dataset.title || '',
      content: slide.dataset.content || '',
    }));

    // Shopify wraps every block rendered via `content_for 'blocks'` in its
    // own `.shopify-block` div, but swiper-container requires its direct
    // children to be exactly <swiper-slide> elements — unwrap those divs
    // so Swiper can find the slides. block.shopify_attributes is already
    // on the <swiper-slide> itself, so editor block-selection still works.
    this.unwrapShopifyBlocks(this.swiperEl);

    this.paginationItems.forEach((item) => {
      item.addEventListener('click', this.onPaginationClick);

      const index = parseInt(item.dataset.index, 10);
      const titleEl = item.querySelector('[data-carousel-pagination-title]');
      if (titleEl && this.slidesData[index]) titleEl.textContent = this.slidesData[index].title;
    });

    const delay = parseInt(this.dataset.autoplayDelay, 10) || 5000;

    Object.assign(this.swiperEl, {
      loop: true,
      autoplay: {
        delay,
        disableOnInteraction: false,
      },
      navigation: {
        prevEl: this.prevButton,
        nextEl: this.nextButton,
      },
    });
    this.swiperEl.initialize();

    const swiper = this.swiperEl.swiper;
    if (!swiper) return;

    swiper.on('slideChange', this.updateInfo);
    swiper.on('autoplayTimeLeft', this.updateProgress);
    this.updateInfo();
  }

  disconnectedCallback() {
    this.paginationItems.forEach((item) => {
      item.removeEventListener('click', this.onPaginationClick);
    });
    this.ctaLink?.removeEventListener('click', this.onCtaClick);
  }

  // Same-page anchors (e.g. "#enquire") smooth-scroll, offset by the fixed
  // header's height (see sticky-nav.js for the same pattern) so the target
  // section isn't left hidden underneath it; anything else (a different
  // page, or no href) just navigates normally.
  onCtaClick = (event) => {
    const href = event.currentTarget.getAttribute('href');
    if (!href || href.charAt(0) !== '#' || href.length < 2) return;

    let target;
    try {
      target = document.querySelector(href);
    } catch (error) {
      return;
    }
    if (!target) return;

    event.preventDefault();

    const rootStyle = getComputedStyle(document.documentElement);
    const announcementBarHeight = parseFloat(rootStyle.getPropertyValue('--announcement-bar-height')) || 0;
    const headerOffset = parseFloat(rootStyle.getPropertyValue('--header-offset')) || 0;
    const targetTop = target.getBoundingClientRect().top + window.scrollY - (announcementBarHeight + headerOffset);

    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  };

  unwrapShopifyBlocks(container) {
    container.querySelectorAll(':scope > .shopify-block').forEach((wrapper) => {
      while (wrapper.firstChild) {
        wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
      }
      wrapper.remove();
    });
  }

  onPaginationClick = (event) => {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isNaN(index)) return;
    this.swiperEl.swiper?.slideToLoop(index);
  };

  updateInfo = () => {
    const swiper = this.swiperEl.swiper;
    if (!swiper) return;

    const data = this.slidesData[swiper.realIndex];
    if (data) {
      if (this.titleEl) this.titleEl.textContent = data.title;
      if (this.contentEl) this.contentEl.innerHTML = data.content;
    }

    if (this.ctaLink) this.ctaLink.hidden = swiper.realIndex !== this.slidesData.length - 1;

    this.paginationItems.forEach((item) => {
      const isActive = parseInt(item.dataset.index, 10) === swiper.realIndex;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');

      if (!isActive) {
        const fill = item.querySelector('[data-carousel-progress-fill]');
        if (fill) fill.style.transform = 'scaleX(0)';
      }
    });
  };

  updateProgress = (swiper, timeLeft, percentage) => {
    const activeItem = this.paginationItems.find((item) => parseInt(item.dataset.index, 10) === swiper.realIndex);
    const fill = activeItem?.querySelector('[data-carousel-progress-fill]');
    if (!fill) return;

    fill.style.transform = `scaleX(${1 - percentage})`;
  };
}

customElements.define('process-carousel', ProcessCarousel);
